/**
 * Resources-Score: BWA-basierte Tragfähigkeit.
 *
 * Heuristik orientiert an Konzept §5.3 (computeResourcesScore-Beispiel),
 * angepasst auf MoneyCents (Integer-Cents) statt Float-Euro:
 *
 *   - Basis 20
 *   - Profit-Stufen (∅ Monatsgewinn = ∅ Umsatz - ∅ Kosten in Cents):
 *       * ≥ 1.500€ (150_000 Cent): +35
 *       * ≥ 800€  (80_000 Cent):   +25
 *       * ≥ 400€  (40_000 Cent):   +12
 *       * sonst:                    +0
 *   - Liquiditätsreichweite (Runway = liquide Mittel ÷ Burn-Rate,
 *     Konzept §4.2). Statt einer binären „Differenz > X €"-Heuristik
 *     wird die Reichweite in Monaten gestaffelt bewertet:
 *       * ≥ 6 Monate: +20
 *       * ≥ 3 Monate: +12
 *       * ≥ 1 Monat:  +5
 *       * sonst:       +0
 *     Falls Burn-Rate (≈ monatliche Kosten) nicht ableitbar oder 0 →
 *     keine Reichweitenbewertung.
 *   - Auslastung: ≥ 100 abrechenbare Stunden/Monat: +15
 *   - Bereinigter Stundensatz (Profit/Stunden) ≥ 30€: +15
 *
 * Gewichtung dieser Dimension im Total-Score: 30% (Konzept §5.2).
 *
 * Wenn `financialDataset` fehlt (z.B. Berater hat noch keine BWA),
 * wird der Basisscore 20 zurückgegeben. Das spiegelt das Datenmodell
 * korrekt wider — niedriger Score bei fehlenden Daten, dazu separat
 * niedrige Konfidenz aus `computeConfidence`.
 *
 * Mittelwerte über mehrere Monate werden über den `avgCents`-Helper
 * gebildet, um Float-Drift in MoneyCents-Aggregationen auszuschließen
 * (siehe internal/money.ts).
 */
import type { ScoreValue } from '../schema/brands.js';
import type { FinancialDataset } from '../schema/financialDataset.js';
import { clampScore } from './internal/clamp.js';
import { avgCents } from './internal/money.js';

const PROFIT_TIER_3_CENTS = 150_000;
const PROFIT_TIER_2_CENTS = 80_000;
const PROFIT_TIER_1_CENTS = 40_000;
const RUNWAY_EXCELLENT_MONTHS = 6;
const RUNWAY_HEALTHY_MONTHS = 3;
const RUNWAY_MIN_MONTHS = 1;
const HOURS_THRESHOLD = 100;
const HOURLY_RATE_THRESHOLD_EUROS = 30;

export function computeResourcesScore(financialDataset: FinancialDataset | undefined): ScoreValue {
  let score = 20;

  if (!financialDataset) return clampScore(score);

  const months = financialDataset.monthlyAggregates;
  const avgRevenueCents = avgCents(months.map((m) => m.revenueCents));
  const avgCostsCents = avgCents(months.map((m) => m.costsCents));
  // Differenz zweier MoneyCents bleibt Integer-konform — kein Math.round nötig,
  // weil avgCents bereits gerundete Integer-Cents liefert.
  const avgProfitCents = (avgRevenueCents as number) - (avgCostsCents as number);

  if (avgProfitCents >= PROFIT_TIER_3_CENTS) score += 35;
  else if (avgProfitCents >= PROFIT_TIER_2_CENTS) score += 25;
  else if (avgProfitCents >= PROFIT_TIER_1_CENTS) score += 12;

  // Liquiditäts-Reichweite (§4.2 Konzept): wie viele Monate trägt die
  // Liquidität die Burn-Rate? Gestaffelte Heuristik statt binärer
  // „Asset-Diff > X"-Logik — eine Reichweite von 6+ Monaten ist deutlich
  // robuster als „gerade so 1 Monat".
  const liq = financialDataset.liquidity;
  if (liq) {
    const burnRateCents = (avgCostsCents as number) || 0;
    if (burnRateCents > 0) {
      const runwayMonths = (liq.liquidAssetsCents as number) / burnRateCents;
      if (runwayMonths >= RUNWAY_EXCELLENT_MONTHS) score += 20;
      else if (runwayMonths >= RUNWAY_HEALTHY_MONTHS) score += 12;
      else if (runwayMonths >= RUNWAY_MIN_MONTHS) score += 5;
    }
  }

  const hours = financialDataset.hourlyRate.billableHoursPerMonth as number | undefined;
  if (typeof hours === 'number' && hours >= HOURS_THRESHOLD) score += 15;

  if (typeof hours === 'number' && hours > 0) {
    const profitEuroPerHour = avgProfitCents / 100 / hours;
    if (profitEuroPerHour >= HOURLY_RATE_THRESHOLD_EUROS) score += 15;
  }

  return clampScore(score);
}
